import DetailsPageLowerLayer from "../../../Components/DetailsPageLowerLayer/DetailsPageLowerLayer";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import Toolbar from "../../../CommonComponents/common/Toolbar";
import TopSection from "../../../CommonComponents/TopSection";
import { useNavigate, useParams } from "react-router-dom";
import { useMemo, useState, useEffect } from "react";
import CollapsibleHeader from "../../../CommonComponents/common/CollapsibleHeader";
import useRedirect from "../../../CustomHooks/useRedirect";
import { Box } from "@mui/material";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import InboundOrderHeader from "./InboundOrderHeader";
import SectionSpinner from "../../../CommonComponents/common/Loader/SectionSpinner";
import {
  fetchHeaderAndLinesData,
  fetchDeliveryData,
  fetchShipmentData,
} from "../../../services/InboundOrders/InboundOrderDetailServices";
import { formatValue } from "../../../utils/currencyUtils";
import { parseDateValue } from "../../../utils/commonFunctions";

const InboundOrderDetail = () => {
  const navigate = useNavigate();
  const redirect = useRedirect();
  let { type, id } = useParams();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [headerData, setHeaderData] = useState({});
  const [allLinesRows, setAllLinesRows] = useState([]);
  const [allDeliveriesRows, setAllDeliveriesRows] = useState([]);
  const [allShipmentsRows, setAllShipmentsRows] = useState([]);
  const [exceptionType, setExceptionType] = useState("");
  // Separate loading states for each section
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [loadingLines, setLoadingLines] = useState(false);
  const [loadingDeliveries, setLoadingDeliveries] = useState(false);
  const [loadingShipments, setLoadingShipments] = useState(false);
  const isStockTransfer = type === "stockTransferPO";

  const breadcrumbArray = [
    "Supply",
    "Purchase Orders",
    "Purchase Orders Detail",
  ];

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/core/supply/purchase-orders");
    }
  };

  const redirectFn = useMemo(() => {
    return (path, state) => {
      redirect(path, state);
    };
  }, [redirect]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingDetails(true);
        setLoadingLines(true);
        const headerAndLinesResponse = await fetchHeaderAndLinesData(type, id);
        if (headerAndLinesResponse.error) {
          console.error(
            "Error fetching header and lines:",
            headerAndLinesResponse.error
          );
          //return;
        }
        setLoadingDetails(false);
        setHeaderData(headerAndLinesResponse);
        setExceptionType(headerAndLinesResponse?.exceptionType);

        const formattedLinesRows = headerAndLinesResponse?.supplyOrderLine?.map(
          (item) => ({
            lineNo: item.lineNo,
            itemType: item.itemType,
            item: item.item,
            returnableContainer: item.returnableContainer,
            itemName: item.itemName,
            materialGroup: item.materialGroup,
            shipFrom: item.shipFrom,
            to: item.to,
            orderQty: formatValue(item.orderQuantity),
            orderUom: item.orderUom,
            unitPrice: formatValue(item.unitPrice),
            extAmount: formatValue(item.extAmount),
            currency: item.currency,
            requestedDate: item.requestedDate,
            lineState: item.lineState,
            totalOpenQty: formatValue(item.totalOpenQuantity),
            totalConfirmedQty: formatValue(item.totalConfirmedQuantity),
            totalReceivedQty: formatValue(item.totalReceivedQuantity),
            originalConfirmedQty: formatValue(item.originalConfirmedQuantity),
            originalConfirmedDelvDate: item.originalConfirmedDelvDate,
            // delayReason: item.delayReason,
            contractNo: item.contractNo,
            contractItem: formatValue(item.contractItem),
            deliveryComplete: item.deliveryComplete ? "Yes" : "No",
            incoTerms1: item.incoTerms1,
            incoTerms2: item.incoTerms2,
            materialAvailabilityDate: item.materialAvailabilityDate,
            exception: item.exception,
            receiptDate: item.receiptDate
          })
        );
        setLoadingLines(false);
        setAllLinesRows(formattedLinesRows || []);

        setLoadingDeliveries(true);
        const deliveryResponse = await fetchDeliveryData(id);
        if (deliveryResponse.error) {
          console.error("Error fetching deliveries:", deliveryResponse.error);
          setAllDeliveriesRows([]);
        } else {
          const formattedDeliveriesRows = deliveryResponse?.map((item) => ({
            deliveryNo: item.deliveryNo,
            deliveryType: item.deliveryType,
            lob: item.lob,
            supplier: item.supplier,
            supplierName: item.supplierName,
            customer: item.customer,
            customerName: item.customerName,
            shipFrom: item.shipFrom,
            shipFromOwner: item.shipFromOwner,
            shipFromSite: item.shipFromSite,
            shipTo: item.shipTo,
            shipToOwner: item.shipToOwner,
            shipToSite: item.shipToSite,
            shipmentNo: item.shipmentNo,
            shipmentLineNo: item.shipmentLineNo,
            shipMode: item.shipMode,
            bolNo: item.bolNo,
            hawbNo: item.hawbNo,
            carrier: item.carrier,
            containerNo: item.containerNo,
            packingSlipNo: item.packingSlipNo,
            plannedShipDate: item.plannedShipDate,
            actualShipDate: item.actualShipDate,
            eta: item.eta,
            ata: item.ata,
            truckNo: item.truckNo,
            trailerNo: item.trailerNo,
            creationDate: item.creationDate,
            comments: item.comments,
            exception: item.exception,
          }));
          setAllDeliveriesRows(formattedDeliveriesRows || []);
        }
        setLoadingDeliveries(false);

        setLoadingShipments(true);
        const shipmentResponse = await fetchShipmentData(id);
        if (shipmentResponse.error) {
          console.error("Error fetching shipments:", shipmentResponse.error);
          setAllShipmentsRows([]);
        } else {
          const formattedShipmentsRows = shipmentResponse?.map((item) => ({
            shipmentNo: item.shipmentNo,
            supplier: item.supplier,
            customer: item.customer,
            carrier: item.carrier,
            plannedShipDate: item.plannedShipDate,
            headerState: item.headerState,
            eta: item.eta,
            pta: item.pta,
            creationDate: item.creationDate,
            notes: item.notes,
            lmd: item.lmd,
            orderLine: item.orderLine,
            shipmentType: item.shipmentType,
            exception: item.exception,
          }));
          setAllShipmentsRows(formattedShipmentsRows || []);
        }
        setLoadingShipments(false);
      } catch (e) {
        console.error("An unexpected error occurred during data fetching:", e);
      } finally {
      }
    };

    if (id) {
      fetchData();
    }
  }, [id, type]);

  // --- MOVED ALL useMemo HOOKS HERE (BEFORE conditional return) ---

  const linesTab = useMemo(
    () => ({
      label: `Lines(${allLinesRows.length})`,
      loading: loadingLines,
      rows: allLinesRows,
      columns: [
        { field: "lineNo", headerName: "Line No" },
        { field: "itemType", headerName: "Item Type" },
        {
          field: "item",
          headerName: "Material Number",
          cellRenderer: (params) => {
            const { value, data } = params;
            const redirect = params.colDef.cellRendererParams?.redirect;
            const siteCode = data?.to ?? " ";
            return (
              <span
                onClick={() =>
                  redirect(
                    `/core/inventory/node/inventory/inventory-detail/${value}/${siteCode}`,
                    {
                      menu: "Inventory",
                      label: "Inventory List",
                      outerLabel: "Inventory Detail",
                      previousPage: window.location.pathname,
                    }
                  )
                }
                style={{
                  color: "#1976d2",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                {value}
              </span>
            );
          },
          cellRendererParams: {
            redirect: redirectFn,
          },
        },
        // { field: "returnable_container", headerName: "Returnable Container" },
        {
          field: "itemName",
          headerName: "Material Description",
          flex: 1,
          cellRenderer: (params) => (
            <Box
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {params.value}
            </Box>
          ),
        },
        { field: "materialGroup", headerName: "Material Group" },
        { field: "shipFrom", headerName: "Ship From" },
        { field: "to", headerName: "To" },
        { field: "orderQty", headerName: "Order Qty" },
        { field: "orderUom", headerName: "Order Uom" },
        { field: "unitPrice", headerName: "Unit Price" },
        { field: "extAmount", headerName: "Ext Amount" },
        { field: "currency", headerName: "Currency" },
        { field: "requestedDate", headerName: "Requested Date",
        comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB) },
        { field: "lineState", headerName: "Line State" },
        { field: "totalOpenQty", headerName: "Total Open Qty" },
        { field: "totalConfirmedQty", headerName: "Total Confirmed Qty" },
        { field: "totalReceivedQty", headerName: "Total Received Qty" },
        {
          field: "originalConfirmedQty",
          headerName: "Original Confirmed Qty",
        },
        {
          field: "originalConfirmedDelvDate",
          headerName: "Original Confirmed Delv Date",
          comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB)
        },
        // { field: "delayReason", headerName: "Delay Reason" },
        !isStockTransfer && { field: "contractNo", headerName: "Contract No" },
        !isStockTransfer && { field: "contractItem", headerName: "Contract Item" },
        { field: "deliveryComplete", headerName: "Delivery Complete" },
        { field: "incoTerms1", headerName: "Inco Terms 1" },
        { field: "incoTerms2", headerName: "Inco Terms 2" },
        !isStockTransfer && {
          field: "materialAvailabilityDate",
          headerName: "Material Availability Date",
          comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB)
        },
        isStockTransfer && { field: "receiptDate", headerName: "Receipt Date",
          comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB) },
      ].filter(Boolean), // Filter out any undefined fields
      // toolbar: {
      //   buttons: [
      //     {
      //       label: "Save Order",
      //       onClick: () => {},
      //       disabled: true,
      //     },
      //     {
      //       label: "Update Open Quantity",
      //       onClick: () => {},
      //       disabled: true,
      //     },
      //     {
      //       label: "Update Delay Reason",
      //       onClick: () => {},
      //       disabled: true,
      //     },
      //   ],
      // },
    }),
    [redirectFn, allLinesRows, loadingLines, isStockTransfer]
  );

  const deliveriesTab = useMemo(
    () => ({
      label: `Deliveries(${allDeliveriesRows.length})`,
      loading: loadingDeliveries,
      rows: allDeliveriesRows,
      columns: [
        {
          field: "deliveryNo",
          headerName: "Delivery No",
          cellRenderer: (params) => {
            const { value } = params;
            return (
              <div
                style={{
                  borderLeft: "5px solid black",
                  paddingLeft: 8,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <span
                  onClick={() =>
                    redirect(`/core/supply/delivery/delivery-detail/${value}`, {
                      menu: "Supply",
                      label: "Inbound Deliveries",
                      outerLabel: "Inbound Deliveries Detail",
                      previousPage: window.location.pathname,
                    })
                  }
                  style={{
                    color: "#1976d2",
                    textDecoration: "underline",
                    cursor: "pointer",
                  }}
                >
                  {value}
                </span>
              </div>
            );
          },
        },
        { field: "deliveryType", headerName: "Delivery Type" },
        { field: "lob", headerName: "LOB" },
        { field: "supplier", headerName: "Supplier" },
        { field: "supplierName", headerName: "Supplier Name" },
        { field: "customer", headerName: "Customer" },
        { field: "customerName", headerName: "Customer Name" },
        { field: "shipFrom", headerName: "Ship From" },
        { field: "shipFromOwner", headerName: "Ship From Owner" },
        { field: "shipFromSite", headerName: "Ship From (Site)" },
        { field: "shipTo", headerName: "Ship To" },
        { field: "shipToOwner", headerName: "Ship To Owner" },
        { field: "shipToSite", headerName: "Ship To (Site)" },
        { field: "shipmentNo", headerName: "Shipment No" },
        { field: "shipmentLineNo", headerName: "Shipment Line No" },
        { field: "shipMode", headerName: "Ship Mode" },
        // { field: "bolNo", headerName: "BOL No" },
        // { field: "hawbNo", headerName: "HAWB No" },
        { field: "carrier", headerName: "Carrier" },
        // { field: "containerNo", headerName: "Container No" },
        // { field: "packingSlipNo", headerName: "Packing Slip No" },
        { field: "plannedShipDate", headerName: "Planned Ship Date",
        comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB) },
        { field: "actualShipDate", headerName: "Actual Ship Date",
        comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB) },
        { field: "eta", headerName: "ETA",
        comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB) },
        { field: "ata", headerName: "ATA",
        comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB) },
        // { field: "truckNo", headerName: "Truck No" },
        // { field: "trailerNo", headerName: "Trailer No" },
        { field: "creationDate", headerName: "Creation Date",
        comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB) },
        // { field: "comments", headerName: "Comments" },
      ],
    }),
    [redirect, allDeliveriesRows, loadingDeliveries]
  );

  const shipmentsTab = useMemo(
    () => ({
      label: `Shipments(${allShipmentsRows.length})`,
      loading: loadingShipments,
      rows: allShipmentsRows,
      columns: [
        {
          field: "shipmentNo",
          headerName: "Shipment No",
          cellRenderer: (params) => {
            const { value } = params;
            return (
              <div
                style={{
                  borderLeft: "5px solid black",
                  paddingLeft: 8,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <LocalShippingOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
                <span
                  onClick={() =>
                    redirect(`/core/supply/shipment/shipment-detail/${value}`, {
                      menu: "Supply",
                      label: "Inbound Shipments",
                      outerLabel: "Inbound Shipments Detail",
                      previousPage: window.location.pathname,
                    })
                  }
                  style={{
                    color: "#1976d2",
                    textDecoration: "underline",
                    cursor: "pointer",
                  }}
                >
                  {value}
                </span>
              </div>
            );
          },
        },
        { field: "supplier", headerName: "Supplier" },
        { field: "customer", headerName: "Customer" },
        { field: "carrier", headerName: "Carrier" },
        { field: "plannedShipDate", headerName: "Planned Ship Date",
        comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB) },
        { field: "headerState", headerName: "Header State" },
        { field: "eta", headerName: "ETA",
        comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB) },
        { field: "pta", headerName: "PTA",
        comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB) },
        { field: "creationDate", headerName: "Creation Date",
        comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB) },
        { field: "notes", headerName: "Notes" },
        { field: "lmd", headerName: "LMD" ,
        comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB)},
      ],
    }),
    [redirect, allShipmentsRows, loadingShipments]
  );

  const documentsTab = useMemo(() => {
    const allDocumentsRows = [];
    return {
      label: "Documents(0)",
      columns: [
        { field: "document_type", headerName: "Document Type" },
        { field: "document_name", headerName: "Document Name" },
        { field: "uploaded_by", headerName: "Uploaded By" },
        { field: "upload_date", headerName: "Upload Date",
        comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB) },
        { field: "status", headerName: "Status" },
      ],
      rows: allDocumentsRows,
      toolbar: {
        icons: [
          {
            type: "add",
            onClick: () => { },
            tooltip: "Add",
            disabled: true,
          },
          {
            type: "edit",
            onClick: () => { },
            tooltip: "Edit",
            disabled: true,
          },
          {
            type: "delete",
            onClick: () => { },
            tooltip: "Delete",
            disabled: true,
          },
        ],
        buttons: [
          {
            label: "Approve",
            onClick: () => { },
            disabled: true,
          },
          {
            label: "Reject",
            onClick: () => { },
            disabled: true,
          },
        ],
        search: {
          placeholder: "Search",
          onChange: (value) => {
            console.log("Searching:", value);
          },
        },
        filter: {
          onClick: () => {
            console.log("Filter clicked");
          },
        },
      },
    };
  }, []);

  const tabs = [linesTab, deliveriesTab, shipmentsTab];

  const toolbarProps = {
    number: id,
    status: exceptionType,
    icon: <ReceiptLongIcon />,
    onBack: handleBack,
    isCollapsed,
    toggleHeader: () => setIsCollapsed((prev) => !prev),
  };

  return (
    <div>
      <TopSection
        breadcrumbArray={breadcrumbArray}
        hideHeader={true}
        headerName={""}
        hideTitle={true}
      />
      <Toolbar {...toolbarProps} />
      <CollapsibleHeader
        upperLayer={
          <Box sx={{ position: "relative" }}>
            {loadingDetails && <SectionSpinner />}
            <InboundOrderHeader hProps={headerData} />
          </Box>}
        lowerLayer={<DetailsPageLowerLayer tabs={tabs} />}
        isCollapsed={isCollapsed}
      />
    </div>
  );
};

export default InboundOrderDetail;
